// Per-user local cache for chat conversations and messages. The server is the
// source of truth (chat_messages, capped at 100 messages per conversation pair);
// this cache is bounded to match so it can't grow past what a fresh server fetch
// would return. Namespaced by userId so dual-login sessions don't share chat
// data across accounts.

import { safeSetItem } from '../localStorageQuota';

const PREFIX = 'valkenhall-chat-v1';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const PER_FRIEND_CAP = 100; // match the server's chat_messages cap (100/pair)

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  // Quota-safe: prunes redundant caches and retries on a full quota instead of
  // silently dropping the write.
  safeSetItem(key, JSON.stringify(value));
}

function messagesKey(userId, friendId) {
  return `${PREFIX}::${userId}::messages::${friendId}`;
}

function conversationsKey(userId) {
  return `${PREFIX}::${userId}::conversations`;
}

function msgTimestamp(msg) {
  const t = msg?.createdAt ? new Date(msg.createdAt).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
}

function pruneStale(messages) {
  const cutoff = Date.now() - TTL_MS;
  return messages.filter((m) => {
    if (!m || !m.id) return false;
    if (typeof m.id === 'string' && m.id.startsWith('pending-')) return false;
    return msgTimestamp(m) >= cutoff;
  });
}

function sortByTime(messages) {
  return [...messages].sort((a, b) => msgTimestamp(a) - msgTimestamp(b));
}

export function getCachedMessages(userId, friendId) {
  if (!userId || !friendId) return [];
  const entry = safeGet(messagesKey(userId, friendId));
  if (!entry || !Array.isArray(entry.messages)) return [];
  return pruneStale(entry.messages);
}

export function saveCachedMessages(userId, friendId, messages) {
  if (!userId || !friendId || !Array.isArray(messages)) return;
  const pruned = pruneStale(messages);
  const trimmed = pruned.length > PER_FRIEND_CAP
    ? pruned.slice(pruned.length - PER_FRIEND_CAP)
    : pruned;
  safeSet(messagesKey(userId, friendId), {
    messages: trimmed,
    savedAt: Date.now(),
  });
}

// Merge server messages onto the cached set. Server entries override
// cached ones with the same id (so fields like `claimed` stay current).
// Returns the merged, time-sorted list ready for setState.
export function mergeMessages(cached, incoming) {
  const byId = new Map();
  for (const m of cached || []) {
    if (m && m.id) byId.set(m.id, m);
  }
  for (const m of incoming || []) {
    if (m && m.id) byId.set(m.id, m);
  }
  return sortByTime([...byId.values()]);
}

export function getCachedConversations(userId) {
  if (!userId) return null;
  const entry = safeGet(conversationsKey(userId));
  if (!entry || !Array.isArray(entry.conversations)) return null;
  return entry.conversations;
}

export function saveCachedConversations(userId, conversations) {
  if (!userId || !Array.isArray(conversations)) return;
  safeSet(conversationsKey(userId), {
    conversations,
    savedAt: Date.now(),
  });
}
